import React, { useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Combobox } from "../components/ui/combobox";
import { useGetCapacityDashboardQuery } from "../store/api/dashboardApi";
import Chart from "react-apexcharts";
import { useGetBomListQuery } from "../store/api/bomApi";
import Datatable from "../components/Datatable";
import { Button } from "../components/ui/button";
import {
  Building2,
  ChevronsUpDownIcon,
  Download,
  ReceiptText,
} from "lucide-react";
import SearchInput from "../components/SearchInput";
import CustomerDropdown from "../components/CustomerDropdown";
import { useGetCustomerNamesQuery } from "../store/api/customerApi";

const CHART_COLORS = {
  total: "#c7d2fe",
  available: "#818cf8",
  allocated: "#4f46e5",
  deployed: "#3730a3",
};

const BillingDashboard = () => {
  return (
    <div className="">
      <h1 className="text-md font-bold mb-4">Billing Dashboard</h1>
      <Tabs defaultValue="billing">
        <TabsList className="mb-4">
          <TabsTrigger className="min-w-40" value="billing">
            Billing
          </TabsTrigger>
          <TabsTrigger className="min-w-40" value="utilization">
            Utilization
          </TabsTrigger>
        </TabsList>
        <TabsContent value="billing">
          <BillingContainer />
        </TabsContent>
        <TabsContent value="utilization">
          <CustomerCapacityContainer />
        </TabsContent>
      </Tabs>
    </div>
  );
};

function BillingContainer() {
  const [selected, setSelected] = useState(null);
  return (
    <div className="grid grid-cols-11 gap-4">
      <div className="col-span-5">
        <OrdersList onSelect={setSelected} />
      </div>
      <div className="col-span-6">
        <BillingDetails selected={selected} />
      </div>
    </div>
  );
}

function OrdersList({ onSelect }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const limit = 10;

  const { data, isLoading, error } = useGetBomListQuery({
    limit,
    offset: (page - 1) * limit,
    search,
  });

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const columns = [
    {
      id: "id",
      label: "BOM ID",
      headClass: "w-[150px]",
      cellClass: "font-medium truncate",
    },
    // {
    //   id: "projectId",
    //   label: "Project ID",
    //   headClass: "min-w-[150px]",
    // },
    {
      id: "customer",
      label: "Customer",
      render: (row) => row.project.customer.organization,
    },
    {
      id: "startDate",
      label: "Date",
      headClass: "w-[90px]",
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <SearchInput search={search} handleSearch={handleSearch} />
      </div>
      <Datatable
        limit={limit}
        page={page}
        setPage={setPage}
        columns={columns}
        rows={
          data?.data?.map((item) => ({
            ...item,
            projectTitle: item.project.title,
          })) || []
        }
        isLoading={isLoading}
        error={error}
        total={data?.pagination?.total || 0}
        headClass="h-8 text-xs !px-2"
        cellClass="!p-2"
        rowClass="cursor-pointer data-[selected=true]:bg-indigo-200 data-[selected=true]:text-indigo-800"
        onRowClick={(row) => {
          // console.log(row);
          onSelect(row.id);
        }}
      />
    </div>
  );
}

function BillingDetails({ selected }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" disabled={selected !== "B-000000000001"}>
          <Download className="w-4 mr-1" />
          Download BOM
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={selected !== "B-000000000001"}
        >
          <ReceiptText className="w-4 mr-1" />
          Upload Invoice
        </Button>
      </div>
      <div className="bg-gray-100 p-3 space-y-2 mx-auto rounded-xl min-h-[400px]">
        {selected == "B-000000000001" ? (
          <>
            {/* Header */}
            <div className="flex justify-between w-full mb-4">
              <div>
                <span className="leading-tight text-xs text-gray-500 font-medium">
                  BOM ID
                </span>
                <h1 className="leading-none text-md font-semibold">
                  B-9879876362006
                </h1>
              </div>
              <div>
                <span className="leading-tight text-xs text-gray-500 font-medium">
                  Project ID
                </span>
                <h1 className="leading-none text-md font-semibold">
                  P-426614174005
                </h1>
              </div>
              <div className="min-w-[120px] shrink-0">
                <span className="leading-tight text-xs text-gray-500 font-medium">
                  Order Date
                </span>
                <h1 className="leading-none text-md font-semibold">
                  01-12-2025
                </h1>
              </div>
            </div>

            <Card className="py-2 gap-2 rounded-lg">
              <CardContent className="px-2 divide-y">
                <div className="flex items-start space-x-2">
                  <Building2 className="w-4 shrink-0 text-gray-500" />
                  <p className="text-sm break-words leading-normal">
                    BharatGen Technology Foundation
                    <span className="block text-gray-800">(ACC-202455)</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card className="py-2 gap-2 rounded-lg">
              <CardHeader className="px-2">
                <CardTitle className="text-sm font-medium text-gray-800">
                  Line items
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="font-medium text-gray-500 pb-2 px-2">
                        Service
                      </th>
                      <th className="font-medium text-gray-500 pb-2 px-2">
                        Units
                      </th>
                      <th className="font-medium text-gray-500 pb-2 px-2">
                        Unit price
                      </th>
                      <th className="font-medium text-gray-500 pb-2 px-2">
                        Line total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="px-2">H100PCIe.8x</td>
                      <td className="px-2">100</td>
                      <td className="px-2">₹ 1,000.00</td>
                      <td className="px-2">₹ 1,00,000.00</td>
                    </tr>
                    <tr>
                      <td className="px-2">Storage (GB-month)</td>
                      <td className="px-2">500</td>
                      <td className="px-2">₹ 20.00</td>
                      <td className="px-2">₹ 10,000.00</td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Allocations */}
            <Card className="py-2 gap-2 rounded-lg">
              <CardHeader className="px-2">
                <CardTitle className="text-sm font-medium text-gray-800">
                  Allocations
                </CardTitle>
              </CardHeader>
              <CardContent className="px-2 space-y-2">
                <div className="flex items-end gap-3">
                  <label className="text-sm">Subsidy 100%</label>
                </div>

                <div className="grid grid-cols-12 gap-2 text-sm">
                  <Card className="col-span-4 p-2 gap-0 rounded-md">
                    <p className="text-gray-500">Gross</p>
                    <p className="font-medium ">₹ 1,10,000.00</p>
                  </Card>

                  <Card className="col-span-4 p-2 gap-0 rounded-md">
                    <p className="text-gray-500">Subsidy Amount</p>
                    <p className="font-medium ">₹ 1,10,000.00</p>
                  </Card>

                  <Card className="col-span-4 p-2 gap-0 rounded-md">
                    <p className="text-gray-500">Net</p>
                    <p className="font-medium ">₹ 0.00</p>
                  </Card>

                  <Card className="col-span-6 p-2 gap-0 rounded-md">
                    <p className="text-gray-500">India AI</p>
                    <p className="font-medium">₹ 1,10,000.00</p>
                  </Card>

                  <Card className="col-span-6 p-2 gap-0 rounded-md">
                    <p className="text-gray-500">Customer</p>
                    <p className="font-medium">₹ 0.00</p>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500 min-h-[400px]">
            Not Available
          </div>
        )}
      </div>
    </div>
  );
}

function CustomerCapacityContainer() {
  const { data, isLoading } = useGetCapacityDashboardQuery();
  const { data: customersData, isLoading: isCustomersLoading } =
    useGetCustomerNamesQuery({});

  const customers = customersData?.map((i) => ({
    label: i.organization,
    value: i.id.toString(),
  }));

  if (isCustomersLoading || !customersData?.length) {
    return <div></div>;
  }

  return (
    <div className="grid grid-cols-12 gap-4 bg-gray-100 rounded-xl p-4">
      <div className="col-span-12">
        <div className="px-0">
          <div>
            <Combobox
              label="Select Customer"
              data={customers}
              buttonVariant="ghost"
              buttonSize="sm"
              popoverAlign="start"
              dropdownIcon={
                <ChevronsUpDownIcon className="ml-1 h-4 w-4 shrink-0 mt-1" />
              }
              classes={{
                button:
                  "w-[400px] text-md text-indigo-600 hover:text-indigo-800 font-medium p-0 h-6",
                popoverContent: "w-[300px]",
                labelContainer: "!justify-start",
              }}
            />
          </div>
          <div className="text-sm  text-gray-600">Compute Utilization</div>
        </div>
      </div>
      <div className="col-span-4">
        {data && <CustomerComputeCapacity data={data.overall} />}
      </div>
      <div className="col-span-8">
        <CustomerServices />
      </div>
    </div>
  );
}

function CustomerComputeCapacity({ data }) {
  const chartData = [
    // { label: "Total", value: data.total, fill: CHART_COLORS.total },
    // { label: "Available", value: data.available, fill: CHART_COLORS.available },
    { label: "Allocated", value: data.allocated, fill: CHART_COLORS.allocated },
    { label: "Deployed", value: data.deployed, fill: CHART_COLORS.deployed },
  ];

  const chartConfig = {
    options: {
      chart: {
        type: "donut",
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
      },
      states: {
        hover: {
          filter: {
            type: "none",
          },
        },
        active: {
          filter: {
            type: "none",
          },
        },
      },
      labels: chartData.map((i) => i.label),
      colors: chartData.map((i) => i.fill),
      dataLabels: {
        dropShadow: {
          enabled: true,
          color: "#000000",
          opacity: 0.2,
          blur: 0.25,
        },
        style: {
          fontSize: "12px",
          fontWeight: 500,
          colors: ["#fff"],
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "70%",
            labels: {
              show: true,
              name: {
                show: true,
                offsetY: -6,
              },
              value: {
                show: true,
                fontSize: "20px",
                fontWeight: 600,
                color: "#111",
                offsetY: 2,
              },
              total: {
                show: true,
                label: "Total Units",
                showAlways: true,
                fontSize: "12px",
                fontWeight: 600,
                color: "#111",
                formatter: () =>
                  chartData.reduce((a, b) => a + Number(b.value), 0),
              },
            },
          },
        },
      },
    },
    series: chartData.map((i) => Number(i.value)),
  };

  return (
    <Card className="py-4 gap-0 flex flex-col">
      <CardHeader className="px-4 items-center pb-0">
        <CardTitle>AI Compute Capacity</CardTitle>
        <CardDescription>Overall</CardDescription>
      </CardHeader>
      <CardContent className="px-2 pb-0">
        <div className="h-full w-full">
          <Chart
            options={chartConfig.options}
            series={chartConfig.series}
            type="donut"
            className="w-full"
            height={305}
            width="100%"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function CustomerServices() {
  return (
    <div className="h-[382px] w-full bg-white rounded-xl border shadow-sm flex items-center justify-center">
      Services Table
    </div>
  );
}

export default BillingDashboard;
